import { useDisclosure } from "@mantine/hooks";
import type { MouseEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { t } from "ttag";

import { useTranslateContent } from "metabase/content-translation/hooks";
import { CollapsibleDashboardParameterList } from "metabase/dashboard/components/CollapsibleDashboardParameterList";
import { DashCardParameterMapper } from "metabase/dashboard/components/DashCard/DashCardParameterMapper/DashCardParameterMapper";
import { useDashboardContext } from "metabase/dashboard/context";
import { useResponsiveParameterList } from "metabase/dashboard/hooks/use-responsive-parameter-list";
import {
  getDashCardInlineValuePopulatedParameters,
  getDashcardParameterMappingOptions,
  getIsEditingParameter,
  getParameterValues,
} from "metabase/dashboard/selectors";
import { useSelector } from "metabase/redux";
import { getSetting } from "metabase/selectors/settings";
import { Box, Ellipsified, Flex } from "metabase/ui";
import { measureTextWidth } from "metabase/utils/measure-text";
import { isEmpty } from "metabase/utils/validate";
import { fillParametersInText } from "metabase/visualizations/shared/utils/parameter-substitution";
import type {
  Dashboard,
  VirtualDashboardCard,
  VisualizationSettings,
} from "metabase-types/api";

import {
  getHeadingFontSize,
  getHeadingFontWeight,
  getTextCardStyle,
} from "../shared/text-card-styles";

import {
  HeadingContent,
  HeadingTextInput,
  InputContainer,
} from "./Heading.styled";

/** `text.align_vertical` is the flex cross-axis on the heading's row container. */
const VERTICAL_ALIGNMENTS: Record<string, string> = {
  top: "flex-start",
  middle: "center",
  bottom: "flex-end",
};

interface HeadingProps {
  isMobile: boolean;
  onUpdateVisualizationSettings: ({ text }: { text: string }) => void;
  dashcard: VirtualDashboardCard;
  settings: VisualizationSettings;
  dashboard: Dashboard;
  /**
   * Absent in the visualization-settings preview, which renders the card without a grid.
   * Optional here for the same reason it is optional on the text card.
   */
  gridSize?: {
    width: number;
    height: number;
  };
}

export function Heading({
  dashboard,
  dashcard,
  settings,
  gridSize,
  onUpdateVisualizationSettings,
  isMobile,
}: HeadingProps) {
  const { isEditing } = useDashboardContext();

  const inlineParameters = useSelector((state) =>
    getDashCardInlineValuePopulatedParameters(state, dashcard?.id),
  );
  const parameterValues = useSelector(getParameterValues);

  const justAdded = useMemo(() => dashcard?.justAdded || false, [dashcard]);

  const tc = useTranslateContent();
  const isShort = (gridSize?.height ?? 0) < 2;

  const [isFocused, { open: toggleFocusOn, close: toggleFocusOff }] =
    useDisclosure(justAdded);
  const isPreviewing = !isFocused;

  const [textValue, setTextValue] = useState(settings.text);
  const preventDragging = (e: MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const isEditingParameter = useSelector(getIsEditingParameter);

  const mappingOptions = useSelector((state) =>
    getDashcardParameterMappingOptions(state, {
      card: dashcard.card,
      dashcard,
    }),
  );
  const hasVariables = mappingOptions.length > 0;

  const translatedText = useMemo(() => tc(settings.text), [settings.text, tc]);

  // handles a case when settings are updated externally
  useEffect(() => {
    setTextValue(settings.text);
  }, [settings.text]);

  const content = useMemo(
    () =>
      isEditing
        ? translatedText
        : fillParametersInText({
            dashcard,
            dashboard,
            parameterValues,
            text: translatedText,
          }),
    [dashcard, dashboard, parameterValues, translatedText, isEditing],
  );

  const hasContent = !isEmpty(settings.text);
  const placeholder = t`You can connect widgets to {{variables}} in heading cards.`;

  const fontFamily = useSelector((state) =>
    getSetting(state, "application-font"),
  );

  const cardStyle = getTextCardStyle(settings);
  const textAlign = settings["text.align_horizontal"] ?? "left";
  const alignItems = VERTICAL_ALIGNMENTS[settings["text.align_vertical"]] ?? "center";

  const { shouldCollapseList, containerRef, parameterListRef } =
    useResponsiveParameterList({
      // measured at the size the heading is actually drawn at — measuring the base size
      // while rendering a scaled one makes the inline filters overlap the text
      reservedWidth: measureTextWidth(content, {
        family: fontFamily,
        size: getHeadingFontSize(settings),
        weight: getHeadingFontWeight(settings),
      }),
    });

  let leftContent: JSX.Element | null;

  if (isEditingParameter) {
    leftContent = (
      <Box h="100%" style={{ overflow: "hidden" }}>
        {hasVariables ? (
          <DashCardParameterMapper
            compact
            dashcard={dashcard}
            isMobile={isMobile}
          />
        ) : (
          <Flex h="100%" display="flex" align="center">
            <Ellipsified>{t`You can connect widgets to {{ variables }} in heading cards.`}</Ellipsified>
          </Flex>
        )}
      </Box>
    );
  } else if (isPreviewing) {
    leftContent = (
      <HeadingContent
        data-testid="editing-dashboard-heading-preview"
        isEditing={isEditing}
        onMouseDown={preventDragging}
        hasFilters={inlineParameters.length > 0}
      >
        {hasContent ? content : placeholder}
      </HeadingContent>
    );
  } else {
    leftContent = (
      <HeadingTextInput
        name="heading"
        data-testid="editing-dashboard-heading-input"
        placeholder={placeholder}
        value={textValue}
        autoFocus={justAdded || isFocused}
        onChange={(e) => setTextValue(e.target.value)}
        onMouseDown={preventDragging}
        onBlur={() => {
          toggleFocusOff();

          if (settings.text !== textValue) {
            onUpdateVisualizationSettings({ text: textValue });
          }
        }}
      />
    );
  }
  if (isEditing) {
    return (
      <InputContainer
        data-testid="editing-dashboard-heading-container"
        isEmpty={!hasContent}
        isPreviewing={isPreviewing}
        onClick={toggleFocusOn}
        ref={containerRef}
        style={{
          paddingRight: shouldCollapseList && isShort ? "2.5rem" : undefined,
          alignItems,
          textAlign,
          ...cardStyle,
        }}
      >
        {leftContent}
        {inlineParameters.length > 0 && (
          <CollapsibleDashboardParameterList
            isCollapsed={shouldCollapseList}
            parameters={inlineParameters}
            ref={parameterListRef}
          />
        )}
      </InputContainer>
    );
  }

  return (
    <Flex
      data-testid="saved-dashboard-heading-container"
      w="100%"
      h="100%"
      justify="space-between"
      pl="0.75rem"
      style={{ overflow: "hidden", alignItems, textAlign, ...cardStyle }}
      ref={containerRef}
    >
      <HeadingContent
        data-testid="saved-dashboard-heading-content"
        hasFilters={inlineParameters.length > 0}
      >
        {content}
      </HeadingContent>
      {inlineParameters.length > 0 && (
        <CollapsibleDashboardParameterList
          isCollapsed={shouldCollapseList}
          parameters={inlineParameters}
          ref={parameterListRef}
        />
      )}
    </Flex>
  );
}
